import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { List, Map } from 'immutable'
import _get from 'lodash/get'
import { getBoundActions } from '../../../actions/index'
import Button, { TYPES } from '../../Button/index'
import { connect as connectStaticText } from '../../StaticText/connect'
import SherpaMessage from '../../SherpaMessage/index'
import { PlanGridCurrency } from '../../PlanGridCurrency/index'
import { getPrimary } from '../../../services/languages/index'
import { formatPrice } from '../../../services/plans/index'
import { get as getConfig } from '../../../config/index'
import { TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM } from '../../../services/dialog/index'
import { cleanSku as cleanSkuService } from '../../../services/checkout/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import PlanValueProp, { hasInlineCode, hasList, hasNestedList } from '../../PlanValueProp/index'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

function getItemClassName (prefix, language) {
  const className = [prefix]

  if (language) {
    className.push(`${prefix}--${language}`)
  }

  return className.join(' ')
}

function getLanguageClassName (prefix, language) {
  const className = [prefix]

  if (language) {
    className.push(`${prefix}--${language}`)
  }

  return className.join(' ')
}

function getButtonClassName (language) {
  const prefix = 'my-account-change-plan-card__button'
  const className = ['button--stacked', prefix, 'button--primary']

  if (language) {
    className.push(`${prefix}--${language}`)
  }

  return className.join(' ')
}

function MyAccountChangePlanCard (props) {
  const {
    plans,
    user,
    sku,
    renderModal,
    gqlPlans,
    currentPlan,
  } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false)
  const [currencyIso, setCurrencyIso] = useState(null)
  const [price, setPrice] = useState(null)
  let plansList = plans.getIn(['data', 'plans'], List()) || List()

  if (planGraphQLActive) {
    plansList = gqlPlans
  }

  const cleanSku = sku ? cleanSkuService(sku) : ''
  const selectedPlan = plansList.filter((val) => cleanSkuService(val.get('sku')) === cleanSku).first() || Map()
  useEffect(() => {
    if (selectedPlan) {
      setCurrencyIso(selectedPlan.get('currencyIso'))
      setPrice((selectedPlan.get('costs', List()) || List()).first())
    }
  }, [selectedPlan])

  function handleClickPlan () {
    const { setPlanChangeSelected } = props
    setPlanChangeSelected(planGraphQLActive ? selectedPlan : selectedPlan.get('sku'))
    renderModal(TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM, {
      hideDismiss: true,
      ...(planGraphQLActive && { currentPlan }),
    })
  }

  const renderPlanGridListItems = () => {
    const {
      staticText,
    } = props
    const valuePropsClassName = 'my-account-change-plan-card__value-list'
    const valuePropositions = selectedPlan.get('valueProps') || List()

    return (
      <div
        key={'offer-1'}
        className={getItemClassName('my-account-change-plan-card__item', userLanguage)}
      >
        <div
          className={getItemClassName(
            'my-account-change-plan-card__item-header',
            userLanguage,
          )}
        >
          <div className="my-account-change-plan-card__heading">
            {selectedPlan.get('title')}
          </div>
          <div className={getLanguageClassName('my-account-change-plan-card__initial-price', userLanguage)}>
            {formatPrice(currencyIso, price, false, null, userLanguage)}
          </div>
        </div>
        <Button
          type={TYPES.PRIMARY}
          onClick={handleClickPlan}
          className={getButtonClassName(userLanguage)}
          scrollToTop
        >
          {staticText.getIn(['data', 'callToAction'])}
        </Button>
        <Button
          type={TYPES.LINK}
          onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
          className={getLanguageClassName('my-account-change-plan-card__details-toggle', userLanguage)}
        >
          {
            isDetailsExpanded
              ? staticText.getIn(['data', 'hidePlanDetails'])
              : staticText.getIn(['data', 'seePlanDetails'])
          }
          <IconV2
            className={`my-account-change-plan-card__icon-down ${isDetailsExpanded ? 'my-account-change-plan-card__icon-down--expanded' : ''}`}
            type={ICON_TYPES.CHEVRON_DOWN}
          />
        </Button>
        <ul className={isDetailsExpanded ? `${valuePropsClassName} ${valuePropsClassName}--expanded` : valuePropsClassName}>
          {valuePropositions.map((val, index) => (
            <li
              className={[
                'my-account-change-plan-card__value-list-item',
                hasInlineCode(val) && 'my-account-change-plan-card__value-list-item--has-code',
                hasList(val) && 'my-account-change-plan-card__value-list-item--has-list',
                hasNestedList(val) && 'my-account-change-plan-card__value-list-item--has-nested-list',
              ].filter(Boolean).join(' ')}
              // eslint-disable-next-line react/no-array-index-key
              key={`my-account-change-plan-card-value-prop-${index}`}
            >
              <PlanValueProp
                valueProp={val}
                className="my-account-change-plan-card__value-list-item-text"
              />
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const renderPlanGridList = () => {
    const { staticText } = props

    if (
      !planGraphQLActive
        && plansList.size === 0
        && (!selectedPlan || (Map.isMap(selectedPlan) && !selectedPlan.size))
    ) {
      return <SherpaMessage message={staticText.getIn(['data', 'loadingPlan'])} arrow={false} center />
    }

    if (
      planGraphQLActive
        && plansList.size === 0
        && (!selectedPlan || (Map.isMap(selectedPlan) && !selectedPlan.size))
    ) {
      return null
    }

    return (
      <div className="my-account-change-plan-card__list">
        {renderPlanGridListItems()}
      </div>
    )
  }

  return (
    <div className="my-account-change-plan-card">
      { currencyIso && <PlanGridCurrency currencyIso={currencyIso} /> }
      <div className="my-account-change-plan-card__list-contatiner">
        {renderPlanGridList()}
      </div>
    </div>
  )
}

MyAccountChangePlanCard.propTypes = {
  plans: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  setPlanChangeSelected: PropTypes.func.isRequired,
  renderModal: PropTypes.func.isRequired,
}

export default compose(
  connectRedux(
    (state) => ({
      plans: state.plans,
      user: state.user,
      userAccount: state.userAccount,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPlanChangeSelected: actions.plans.setPlanChangeSelected,
        renderModal: actions.dialog.renderModal,
      }
    },
  ),
  connectStaticText({ storeKey: 'myAccountChangePlanCard' }),
)(MyAccountChangePlanCard)
